import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Seo, breadcrumbSchema, faqSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { useSiteContent } from "../lib/useSiteContent";
import { ServiceCTA } from "../components/ServiceCTA";

const PATH = "/faq";

const CATEGORIES = [
  {
    title: "Booking & Pricing",
    faqs: [
      {
        question: "How do I get a cleaning quote in London?",
        answer:
          "The fastest way is the form on our contact page or a WhatsApp message with your postcode, property size and the date you need the clean. Most quotes are sent within a couple of working hours. For complex properties — large family homes, post-build, or HMOs — we may ask for a few photos before pricing.",
      },
      {
        question: "Is the published price the final price?",
        answer:
          "Yes. Our quotes are fixed once issued — no on-the-day surprises. The only situation where the price would change is if the property turns out to be materially different from how it was described at quote time. Even in that case, we tell you before any change.",
      },
      {
        question:
          "Do you charge extra for evening, weekend or bank holiday cleans?",
        answer:
          "There is no out-of-hours premium for weekend or evening slots between 8am and 8pm. Bank holiday bookings carry a small surcharge — disclosed up front in the quote — to cover staff overtime on those days.",
      },
      {
        question: "Can I cancel or reschedule my booking?",
        answer:
          "Yes. Free cancellation up to 24 hours before your slot, and free rescheduling up to 12 hours before. Within those windows we may need to charge a small admin fee to cover the cleaner's allocated time. Same-day cancellations are handled on a case-by-case basis.",
      },
      {
        question: "What payment methods do you accept?",
        answer:
          "Bank transfer, debit and credit cards, Apple Pay and Google Pay via Stripe invoice, plus contactless on site. Letting agents and landlord portfolios can be set up with monthly invoicing terms.",
      },
    ],
  },
  {
    title: "Service & Standards",
    faqs: [
      {
        question:
          "Are your cleaners employed and DBS-checked?",
        answer:
          "Yes. Every team member is employed by Prime Tenancy Cleaning — not a sub-contracted freelancer or agency worker. All cleaners are DBS-checked, trained in-house against the AIIC inventory benchmark, and paid the London Living Wage.",
      },
      {
        question: "Do you bring your own equipment and products?",
        answer:
          "Yes. We arrive with professional-grade equipment — HEPA vacuums, dip-tanks for oven cleaning, hot-water carpet extractors and microfibre kits — plus all the chemistry needed for the job. You do not need to provide anything beyond water and electricity.",
      },
      {
        question: "What products do you use? Are they eco-friendly?",
        answer:
          "Our default chemistry is plant-based, biodegradable, fragrance-free and low-fume — safe for households with children, pets or sensitivities. For tougher inventory cleans we may use stronger degreasers off-site in our dip-tanks, but never caustic products inside the oven cavity.",
      },
      {
        question: "Do you offer a guarantee?",
        answer:
          "Yes. Every end of tenancy cleaning booking comes with a 7-day re-clean guarantee — if your landlord or letting agent flags any cleaning issue on the checkout report, we return free of charge to re-clean the flagged area. One-off deep cleaning and after-builders cleaning are covered by a 3-day re-clean guarantee on the same basis.",
      },
      {
        question: "How does your re-clean guarantee actually work?",
        answer:
          "If your landlord or inventory clerk raises a cleaning concern within 7 days of our visit, send us the checkout report or written communication and we'll schedule a free re-clean within 7 days. No fine print, no quibble — provided the issue is cleaning-related and the property has not been re-occupied in the interim.",
      },
    ],
  },
  {
    title: "On the Day",
    faqs: [
      {
        question: "Do I need to be at the property during the clean?",
        answer:
          "No. Most London tenants meet us on arrival, hand over the keys and return at the end of the visit. We are used to working with key safes, lock-boxes, building concierges and letting agent key drops. We can also coordinate directly with your inventory clerk.",
      },
      {
        question: "How long will my cleaning take?",
        answer:
          "Studios take around 3–4 hours, one-bedroom flats 4–6 hours, two-bedroom homes 5–7 hours, and three-bedroom houses 7–9 hours. Heavily soiled properties, large family homes or HMOs may need a second day or an extra cleaner.",
      },
      {
        question: "What if there is a problem with parking or access?",
        answer:
          "Send us your postcode at booking and we'll flag any parking restrictions. Where there is no nearby parking, we factor in a small travel charge from the closest legal bay. Building concierges, lift restrictions and lock-box codes are best confirmed when you book.",
      },
      {
        question: "Can you clean while I am still living in the property?",
        answer:
          "Yes — we routinely work around furniture and personal belongings for deep cleans and end of tenancy jobs where the property is partially packed. For best results, particularly on a checkout, an empty or near-empty property gives the cleanest finish.",
      },
    ],
  },
  {
    title: "End of Tenancy Specifics",
    faqs: [
      {
        question:
          "Will end of tenancy cleaning satisfy my London letting agent's checkout report?",
        answer:
          "In almost every case, yes. We follow the AIIC inventory checklist that major London letting agents use, and we offer a 7-day re-clean guarantee if anything is flagged. The only exception is purely non-cleaning issues — damage, repairs, missing items — which sit outside the cleaning contractor's scope.",
      },
      {
        question:
          "Should I get carpet cleaning if my tenancy agreement says 'professionally cleaned carpets'?",
        answer:
          "Yes. That clause specifically requires hot-water extraction by a third-party contractor with a receipt. A vacuum, a steam mop or a spot-treatment will not satisfy it. We can complete the carpet clean alongside the rest of the tenancy clean and issue a single invoice for the checkout report.",
      },
      {
        question: "Do you provide an invoice for my landlord or letting agent?",
        answer:
          "Yes. Every job is invoiced on company letterhead with the property address, services performed and date of visit — exactly the documentation a checkout report expects. We can address invoices directly to a letting agent or managing company on request.",
      },
      {
        question:
          "How soon before my checkout should I book the cleaning?",
        answer:
          "Ideally 5–10 days ahead, especially around month-end and weekends when slots fill up. That said, we routinely fit in same-day and next-day bookings — particularly for one-bedroom and studio properties — so don't assume it's too late to ask.",
      },
    ],
  },
  {
    title: "Carpet, Oven & Add-Ons",
    faqs: [
      {
        question: "Will steam carpet cleaning shrink or damage my carpets?",
        answer:
          "No. Professional hot-water extraction is the method manufacturers themselves recommend for deep cleaning carpets. We use the correct heat, dwell time and extraction pressure for the fibre type, and we test colour-fast safe materials before starting.",
      },
      {
        question: "Do you clean self-cleaning (pyrolytic) ovens?",
        answer:
          "Yes. We do not use caustic chemistry inside pyrolytic or catalytic ovens — those chemicals damage the special enamel that makes the self-cleaning function work. Our process is dip-tank based for removable parts and uses a non-caustic degreaser inside the cavity.",
      },
      {
        question: "Can I bundle add-ons with my end of tenancy clean?",
        answer:
          "Yes — and you save money doing so. Carpet cleaning, oven cleaning, hob & extractor and inside windows all unlock automatic bundled discounts when booked alongside an end of tenancy package. The discount appears on your quote up front.",
      },
      {
        question:
          "Do you clean upholstery, mattresses and external windows?",
        answer:
          "Upholstery and mattress steam cleaning are bookable as add-ons. Internal windows, frames and sills are included in our end of tenancy package. External windows are bookable for ground-floor and balcony-accessible glass only — higher-level external windows require a specialist abseil or pole contractor.",
      },
    ],
  },
];

const flatFaqs = CATEGORIES.flatMap((c) => c.faqs);

const FAQPage = () => {
  const sc = useSiteContent("faq", {
    eyebrow:    "Help Centre",
    h1:         "London Cleaning",
    highlight:  "Frequently Asked Questions",
    subheading: "Detailed answers to the questions tenants, landlords and letting agents ask before booking us. Can't find what you need? Reach out via our contact page.",
    cat1_title: "Booking & Pricing",
    cat2_title: "Service & Standards",
    cat3_title: "On the Day",
    cat4_title: "End of Tenancy Specifics",
    cat5_title: "Carpet, Oven & Add-Ons",
    still_heading: "Still have a question?",
    still_body:    "Our London team is happy to walk through your tenancy specifics over the phone or WhatsApp. Most questions are answered in minutes.",
  });
  const catTitles = [sc.cat1_title, sc.cat2_title, sc.cat3_title, sc.cat4_title, sc.cat5_title];
  return (
    <>
      <Seo
        title="FAQ — End of Tenancy Cleaning London Questions Answered"
        description="Detailed FAQ for end of tenancy cleaning London. Booking, pricing, standards, guarantees, carpet, oven & deep clean answers for tenants, landlords and letting agents."
        ogTitle="FAQ | Prime Tenancy Cleaning London"
        ogDescription="Everything tenants, landlords and letting agents ask about move-out cleaning in London — pricing, process, standards, guarantees."
        path={PATH}
        schema={[
          breadcrumbSchema([{ name: "FAQ", path: PATH }]),
          faqSchema(flatFaqs),
        ]}
      />

      <PageHero
        eyebrow={sc.eyebrow}
        h1={sc.h1}
        highlight={sc.highlight}
        subheading={sc.subheading}
        breadcrumbs={[{ name: "FAQ", path: PATH }]}
        image="https://images.unsplash.com/photo-1521012108593-cf7f7c0fbb89?w=1920&q=80"
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl mx-auto space-y-16">
            {CATEGORIES.map((cat, ci) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                data-testid={`faq-category-${ci}`}
              >
                <div className="mb-6">
                  <span className="text-teal font-semibold text-sm uppercase tracking-wider">
                    Section {String(ci + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy mt-2">
                    {catTitles[ci] || cat.title}
                  </h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                  {cat.faqs.map((f, i) => (
                    <AccordionItem
                      key={`${ci}-${i}`}
                      value={`${ci}-${i}`}
                      className="bg-slate-50 rounded-xl px-6 border-none"
                      data-testid={`faq-${ci}-${i}`}
                    >
                      <AccordionTrigger className="text-left font-semibold text-navy hover:text-teal hover:no-underline py-5 text-base">
                        {f.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                        {f.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            ))}
          </div>

          {/* Still have questions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto mt-20 bg-slate-50 rounded-2xl p-8 md:p-10 border border-slate-100 text-center"
          >
            <h3 className="font-heading text-2xl font-semibold text-navy mb-3">{sc.still_heading}</h3>
            <p className="text-slate-600 mb-5">{sc.still_body}</p>
            <div className="flex flex-wrap justify-center gap-3 text-sm font-semibold">
              <Link
                to="/contact"
                className="bg-gold hover:bg-gold-dark text-navy px-5 py-3 rounded-xl transition-colors"
              >
                Go to contact page
              </Link>
              <a
                href="tel:+442080363586"
                className="bg-navy hover:bg-navy-light text-white px-5 py-3 rounded-xl transition-colors"
              >
                Call 020 8036 3586
              </a>
              <a
                href="https://wa.me/447346006547"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-navy text-navy hover:bg-navy hover:text-white px-5 py-3 rounded-xl transition-colors"
              >
                WhatsApp us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <ServiceCTA
        title="Ready to book your London clean?"
        description="Fixed quote in a couple of hours, written 7-day re-clean guarantee, weekend and same-day slots available."
        variant="navy"
      />
    </>
  );
};

export default FAQPage;
